import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FaxHistorySearch } from '@app/shared/models';
import { ProductWithLoBAndTenantDTO } from '@app/shared/models/product-lob-tenant.model';
import { AuthenticationService, LookupService } from '@core/services';
import { PlatformService } from '@core/services/platform.service';
import { FeatureToggleService, LineOfBusiness, ProductNames, TenantNames } from '@mynexus/mynexus-ui-lib';
import { SelectItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DatePickerModule } from 'primeng/datepicker';
import { Fluid } from 'primeng/fluid';
import { MultiSelectModule } from 'primeng/multiselect';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SelectModule } from 'primeng/select';
import { forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'app-fax-history-search-form',
    templateUrl: './fax-history-search-form.component.html',
    styleUrls: ['./fax-history-search-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
    ButtonModule,
    DatePickerModule,
    CardModule,
    SelectModule,
    FormsModule,
    MultiSelectModule,
    ProgressSpinnerModule,
    ReactiveFormsModule,
    Fluid
],
    providers: [LookupService, AuthenticationService, PlatformService]
})
export class FaxHistorySearchFormComponent implements OnInit {
  @Input() isLoading = false;

  @Output() submitSearch = new EventEmitter<FaxHistorySearch>();

  readonly defaultUser: string;

  form: FormGroup;
  lineOfBusiness: string;
  isLoadingUsers = false;
  selectedUsers: string[] = [];
  userNameList: SelectItem[] = [];
  productList: SelectItem[] = [];
  faxStatusList: SelectItem[] = [
    { label: 'All', value: null },
    { label: 'Success', value: 'Success' },
    { label: 'Failed', value: 'Failed' },
    { label: 'Aborted', value: 'Aborted' },
    { label: 'Dead', value: 'Dead' },
    { label: 'Submitted', value: 'Submitted' },
    { label: 'Pending', value: 'Pending' }
  ];

  programToolTip = 'This Program field indicates the Program and Line of Business';

  private readonly authenticationService = inject(AuthenticationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly featureToggleService = inject(FeatureToggleService);
  private readonly lookupService = inject(LookupService)
  private readonly platformService = inject(PlatformService);
  private readonly ref = inject(ChangeDetectorRef);

  constructor() {
    this.defaultUser = this.authenticationService.userEmail;
  }

  ngOnInit() {
    this.getProductsWithLineOfBusiness();
    this.isLoadingUsers = true;
    this.ref.markForCheck();

    this.lookupService.getInternalADUsersShortInfoList()
      .pipe(
        finalize(() => {
          this.isLoadingUsers = false;
          this.ref.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(
        (users) => {
          const userIndex = users.findIndex(el => el.emailAddress === this.defaultUser);
          const loggedInUser = userIndex ? users.splice(userIndex, 1) : [];

          if (loggedInUser.length) {
            this.selectedUsers = [loggedInUser[0].emailAddress];

            this.userNameList = [
              ...[{ value: loggedInUser[0].emailAddress, label: loggedInUser[0].name }],
              ...users.map(
                ({ emailAddress, name }) => ({
                  value: emailAddress == null ? 'null (' + name + ')' : emailAddress,
                  label: name
                } as SelectItem)
              )
            ];

            // TODO: mynadmin and portal@ need to be merged together, see #258011
            // For now, we are hiding portal@ and using the existing mynadmin "Portal" user.
            // After migration efforts, all faxes should be using portal@
            this.userNameList.push({ value: 'mynadmin', label: 'Portal' });
            this.userNameList = this.userNameList.filter(user => user.value !== 'portal@mynexuscare.com');
          }
        }
      );

    const today = new Date(Date.now());

    this.form = new FormGroup({
      productLob: new FormControl(null),
      selectedUser: new FormControl(null),
      status: new FormControl(null),
      startDate: new FormControl(today),
      endDate: new FormControl(today)
    });
  }

  onSearch() {
    const { productLob, selectedUser, status, startDate, endDate } = this.form.value;
    const productId = productLob?.split(';')[0];
    const lineOfBusiness = productLob?.split(';')[1];

    // set to start of day
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);

    //convert to UTC
    const startDateUTC = startDate.toUTCString();
    const endDateUTC = endDate.toUTCString();


    this.submitSearch.emit({
      productId,
      selectedUser,
      status,
      startDate: startDateUTC,
      // API automatically add 23 horus 59 minutes and 59 seconds
      // So end date should start as 00:00:00 of current day
      endDate: endDateUTC,
      lineOfBusiness
    } as FaxHistorySearch);
  }

  hideFilterPanel(): void {
    if (!this.selectedUsers.length) {
      this.selectedUsers = [this.defaultUser];
    }
  }

  private getProductsWithLineOfBusiness(): void {
    this.productList = [{ value: null, label: 'ALL' }];

    forkJoin({
      isPACProductLOBMigrationEnabled: this.featureToggleService.getFeatureAccess('PacProductAndLobMigration'),
      productsLOBList: this.platformService.getProductsWithLinesOfBusiness()
    })
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(({ isPACProductLOBMigrationEnabled, productsLOBList }) => {
      // Right now, fax history does not filter by tenant.  Anthem is being used since all products have an Anthem Medicare Advantage lob.
      // The functionality below allows the possibility of filtering by tenant in the future.
      const filteredProductList = productsLOBList.filter(x => x.tenantName === TenantNames.Anthem);

      if (this.authenticationService.isPACOperations) {
        this.buildProductLineOfBusinessList(filteredProductList.find(
          p => p.shortName === ProductNames.HomeHealth && p.lineOfBusinessName === LineOfBusiness.MedicareAdvantage
        ));

        this.buildProductLineOfBusinessList(filteredProductList.find(
          p => p.shortName === ProductNames.PACIM && p.lineOfBusinessName === LineOfBusiness.MedicareAdvantage
        ));
      }

      if (this.authenticationService.isPACCommercialOperations) {
        if (isPACProductLOBMigrationEnabled) {
          this.buildProductLineOfBusinessList(filteredProductList.find(
            p => p.shortName === ProductNames.PACIM && p.lineOfBusinessName === LineOfBusiness.Commercial
          ));
        } else {
          this.buildProductLineOfBusinessList(filteredProductList.find(
            p => p.shortName === ProductNames.PacCommercial
          ));
        }
      }

      this.buildProductLineOfBusinessList(filteredProductList.find(
        p => p.shortName === ProductNames.DMEPOS && p.lineOfBusinessName === LineOfBusiness.MedicareAdvantage
      ));

      this.productList.sort((a, b) => a?.label.localeCompare(b?.label));
      this.ref.markForCheck();
    });
  }

  private buildProductLineOfBusinessList(productLobTenant: ProductWithLoBAndTenantDTO): void {
    let productLabel;

    if (productLobTenant.lineOfBusinessName === LineOfBusiness.MedicareAdvantage) {
      productLabel = productLobTenant.shortName + ' Medicare'
    } else if (productLobTenant.shortName === ProductNames.PacCommercial) {
      productLabel = productLobTenant.shortName;
    } else {
      productLabel = productLobTenant.shortName + ' ' + productLobTenant.lineOfBusinessName;
    }

    const productValue = productLobTenant.productId + ';' + productLobTenant.lineOfBusinessName;

    this.productList.push({ value: productValue, label: productLabel });
  }
}
